import { useEffect, useRef, useState } from 'react';
import { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { retryWithBackoff } from '../../../common/helpers/retryWithBackoff';
import { useAxiosWithAPI } from '../../../common/hooks/useAxiosWithAPI';
import useIsKeywordMatchingEnabled from '../../../common/hooks/useIsKeywordMatchingEnabled';
import { DraftResponse } from '../../../common/interfaces/responses';
import { getSessionCompanyId } from '../../../selectors/session';
import { getUserId } from '../../../selectors/user';
import { JobDraftFormState, JobDraftPostingPayload } from '../common/constants';
import { trackJobDraftRetried } from '../tracking';
import useConvertJobData from './useConvertJobData';

const useDraft = () => {
  const axios = useAxiosWithAPI();
  const { convertJobFormStateToPayload } = useConvertJobData();
  const sessionCompanyId: string = useSelector(getSessionCompanyId);
  const sessionUserId: string = useSelector(getUserId);
  const isJobApiV2Enabled = useIsKeywordMatchingEnabled();

  const [isLoading, setIsLoading] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortControllerRef.current?.abort();
    },
    []
  );

  const onRetry = (attempt: number, error: unknown) => {
    const axiosError = error as AxiosError;
    trackJobDraftRetried({
      attempt,
      statusCode: axiosError.response?.status,
      errorCode: axiosError.code,
      userId: sessionUserId,
      companyId: sessionCompanyId,
    });
  };

  const createDraft = async (
    payload: JobDraftPostingPayload
  ): Promise<DraftResponse> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    try {
      const url = isJobApiV2Enabled ? '/v2/jobDrafts' : '/jobDrafts';
      const res = await retryWithBackoff(
        signal => axios.post(url, { data: payload, signal }),
        3,
        undefined,
        { signal: controller.signal, onRetry }
      );
      return res.data;
    } finally {
      setIsLoading(false);
    }
  };

  const updateDraft = async (
    draftId: string,
    payload: JobDraftPostingPayload
  ): Promise<DraftResponse> => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoading(true);

    try {
      const url = isJobApiV2Enabled
        ? `/v2/jobDrafts/${draftId}`
        : `/jobDrafts/${draftId}`;
      const res = await retryWithBackoff(
        signal => axios.put(url, { data: payload, signal }),
        3,
        undefined,
        { signal: controller.signal, onRetry }
      );
      return res.data;
    } catch (error) {
      if (controller.signal.aborted) throw error;
      throw new Error('Failed to create draft');
    } finally {
      setIsLoading(false);
    }
  };

  const upsertDraft = async (data: JobDraftFormState, jobDraftId?: string) => {
    if (jobDraftId) {
      const response = await updateDraft(
        jobDraftId,
        convertJobFormStateToPayload(data)
      );

      return response.data;
    } else {
      const response = await createDraft(convertJobFormStateToPayload(data));

      return response.data;
    }
  };

  return {
    updateDraft,
    createDraft,
    upsertDraft,
    isLoading,
  };
};

export default useDraft;
